/**
 * Care incident taxonomy (care incidents R1 S4). This is the one TypeScript copy of the closed
 * vocabularies enforced by migrations 20435000002184 and 20435000002556 (unapplied candidate). The web wizard, the edit drawer, the
 * register and the worker app read it from here.
 *
 * The SQL is authoritative and the rules here are its exact mirror:
 * - values match exactly, with no case folding, trimming or type coercion;
 * - a value that does not belong to the type is not stored (never refused);
 * - a value outside the vocabulary is dropped with a warning (delta review B4; never refused).
 * A sub-type is not a category: the FALL and MEDICATION_ERROR counts and the SIRS mapping are
 * unchanged.
 */

/** Exactly the eMAR `medication_incidents.error_type` vocabulary (`mi_error_type_check`). */
export const MEDICATION_INCIDENT_SUBTYPES = [
  'OMISSION',
  'WRONG_MEDICATION',
  'WRONG_DOSE',
  'WRONG_STRENGTH',
  'WRONG_FORM',
  'WRONG_TIME',
  'WRONG_ROUTE',
  'WRONG_CLIENT',
  'EXTRA_DOSE',
  'EXPIRED_MEDICATION',
  'PATCH_NOT_REMOVED',
  'ADVERSE_REACTION',
  'ALLERGY_MEDICATION_GIVEN',
  'PRESCRIBING_ERROR',
  'NO_PRESCRIBER_ORDER',
  'PHARMACY_DISPENSING_ERROR',
  'DOCUMENTATION_ERROR',
  'DD_REGISTER_PREVIOUS_ENTRY_INCORRECT',
  'DD_TWO_PERSON_CHECK_MISSED',
  'DD_SECOND_CHECKER_NOT_WITNESSED',
  'OTHER',
] as const;
export type MedicationIncidentSubtype = (typeof MEDICATION_INCIDENT_SUBTYPES)[number];

/**
 * Provider-facing labels. "Missed dose" is the eMAR OMISSION value. These follow common
 * Australian practice and Outcome 5.3; they are not a mandated taxonomy.
 */
export const MEDICATION_INCIDENT_SUBTYPE_LABELS: Record<MedicationIncidentSubtype, string> = {
  OMISSION: 'Missed dose',
  WRONG_MEDICATION: 'Wrong medication',
  WRONG_DOSE: 'Wrong dose',
  WRONG_TIME: 'Wrong time',
  WRONG_ROUTE: 'Wrong route',
  WRONG_CLIENT: 'Wrong resident',
  EXTRA_DOSE: 'Extra dose',
  EXPIRED_MEDICATION: 'Expired',
  ADVERSE_REACTION: 'Adverse reaction',
  PRESCRIBING_ERROR: 'Prescribing',
  DOCUMENTATION_ERROR: 'Documentation',
  NO_PRESCRIBER_ORDER: 'Given without a valid prescriber order',
  DD_REGISTER_PREVIOUS_ENTRY_INCORRECT: 'Controlled-drug register discrepancy carried forward',
  ALLERGY_MEDICATION_GIVEN: 'Given despite a recorded allergy',
  WRONG_FORM: 'Wrong formulation',
  WRONG_STRENGTH: 'Wrong strength',
  DD_TWO_PERSON_CHECK_MISSED: 'Controlled-drug double check not done',
  DD_SECOND_CHECKER_NOT_WITNESSED:
    'Controlled drug: administration not witnessed by second checker',
  PHARMACY_DISPENSING_ERROR: 'Dispensing error (pharmacy)',
  PATCH_NOT_REMOVED: 'Transdermal patch not removed',
  OTHER: 'Other',
};

export const FALL_OBSERVATIONS = ['WITNESSED', 'UNWITNESSED', 'FOUND_ON_FLOOR', 'UNKNOWN'] as const;
export type FallObservation = (typeof FALL_OBSERVATIONS)[number];
export const FALL_OBSERVATION_LABELS: Record<FallObservation, string> = {
  WITNESSED: 'Witnessed',
  UNWITNESSED: 'Unwitnessed',
  FOUND_ON_FLOOR: 'Found on floor',
  UNKNOWN: 'Not known',
};

export const YES_NO_UNKNOWN = ['YES', 'NO', 'UNKNOWN'] as const;
export type YesNoUnknown = (typeof YES_NO_UNKNOWN)[number];
export const FALL_HEAD_STRIKE_LABELS: Record<YesNoUnknown, string> = {
  YES: 'Head struck',
  NO: 'No head strike',
  UNKNOWN: 'Head strike not known',
};
/**
 * QI Program Manual Part A (September 2026) s 12.2 exclusion: a fall while the person was away from
 * the aged care home and not under direct staff supervision. YES excludes the fall's major injury
 * from the falls-with-major-injury count; NULL = not recorded (counted, and disclosed).
 */
export const FALL_AWAY_UNSUPERVISED_LABELS: Record<YesNoUnknown, string> = {
  YES: 'Away from the home, not supervised by staff',
  NO: 'At the home or supervised by staff',
  UNKNOWN: 'Not known',
};
/**
 * Per affected person (D-8). Major injury (QI Program Manual Part A, Sept 2026, s 12.2): bone
 * fracture, joint dislocation, closed head injury with altered consciousness, or subdural haematoma.
 */
export const FALL_MAJOR_INJURY_LABELS: Record<YesNoUnknown, string> = {
  YES: 'Major injury',
  NO: 'No major injury',
  UNKNOWN: 'Not yet known',
};

/** "Consider SIRS" answers recorded for the provider's reasoning (never a classification). */
export const SIRS_CONSIDERATION_ANSWERS = ['YES', 'NO', 'UNSURE'] as const;
export type SirsConsiderationAnswer = (typeof SIRS_CONSIDERATION_ANSWERS)[number];
export const SIRS_CONSIDERATION_ANSWER_LABELS: Record<SirsConsiderationAnswer, string> = {
  YES: 'Yes',
  NO: 'No',
  UNSURE: 'Not sure',
};
export type SirsConsideration = {
  cause_known?: SirsConsiderationAnswer | null;
  possible_neglect?: SirsConsiderationAnswer | null;
  possible_force?: SirsConsiderationAnswer | null;
  another_resident?: SirsConsiderationAnswer | null;
  reasoning?: string | null;
  consulted?: string | null;
};
export const SIRS_CONSIDERATION_REASONING_MAX = 2000;
export const SIRS_CONSIDERATION_CONSULTED_MAX = 500;

/** Incident types for which the decision prompts are offered (type-only checks cannot answer them). */
export const CONSIDER_SIRS_INCIDENT_TYPES = [
  'MEDICAL_EPISODE',
  'SKIN_INJURY',
  'UNEXPLAINED_INJURY',
  'FALL',
  'PRESSURE_INJURY',
  'BEHAVIOURAL',
  'OTHER',
] as const;

export function isConsiderSirsIncidentType(type: string | null | undefined): boolean {
  return type != null && (CONSIDER_SIRS_INCIDENT_TYPES as readonly string[]).includes(type);
}

const isMember = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (list as readonly string[]).includes(value);

export const isMedicationIncidentSubtype = (value: unknown): value is MedicationIncidentSubtype =>
  isMember(MEDICATION_INCIDENT_SUBTYPES, value);
export const isFallObservation = (value: unknown): value is FallObservation =>
  isMember(FALL_OBSERVATIONS, value);
export const isYesNoUnknown = (value: unknown): value is YesNoUnknown =>
  isMember(YES_NO_UNKNOWN, value);

export type IncidentTaxonomyInput = {
  incidentType: string | null | undefined;
  incidentSubtype?: unknown;
  fallObservation?: unknown;
  fallHeadStrike?: unknown;
  fallAwayUnsupervised?: unknown;
};
export type IncidentTaxonomy = {
  incidentSubtype: MedicationIncidentSubtype | null;
  fallObservation: FallObservation | null;
  fallHeadStrike: YesNoUnknown | null;
  fallAwayUnsupervised: YesNoUnknown | null;
};

/**
 * Normalise taxonomy attributes exactly as the capture recorder does (delta review B4). A present
 * value outside the vocabulary is dropped and named in `dropped`, as SQL drops it with a capture
 * warning; a valid value the type cannot carry is dropped too. Never refuses.
 */
export function normaliseIncidentTaxonomy(input: IncidentTaxonomyInput): {
  ok: true;
  value: IncidentTaxonomy;
  dropped: (keyof IncidentTaxonomy)[];
} {
  const present = (value: unknown) => value !== undefined && value !== null && value !== '';
  const dropped: (keyof IncidentTaxonomy)[] = [];
  if (present(input.incidentSubtype) && !isMedicationIncidentSubtype(input.incidentSubtype)) {
    dropped.push('incidentSubtype');
  }
  if (present(input.fallObservation) && !isFallObservation(input.fallObservation)) {
    dropped.push('fallObservation');
  }
  if (present(input.fallHeadStrike) && !isYesNoUnknown(input.fallHeadStrike)) {
    dropped.push('fallHeadStrike');
  }
  if (present(input.fallAwayUnsupervised) && !isYesNoUnknown(input.fallAwayUnsupervised)) {
    dropped.push('fallAwayUnsupervised');
  }
  const isMedication = input.incidentType === 'MEDICATION_ERROR';
  const isFall = input.incidentType === 'FALL';
  return {
    ok: true,
    dropped,
    value: {
      incidentSubtype:
        isMedication && isMedicationIncidentSubtype(input.incidentSubtype)
          ? input.incidentSubtype
          : null,
      fallObservation:
        isFall && isFallObservation(input.fallObservation) ? input.fallObservation : null,
      fallHeadStrike: isFall && isYesNoUnknown(input.fallHeadStrike) ? input.fallHeadStrike : null,
      fallAwayUnsupervised:
        isFall && isYesNoUnknown(input.fallAwayUnsupervised) ? input.fallAwayUnsupervised : null,
    },
  };
}

/**
 * The server keyword safety net (capture recorder, core correction and triage amend in
 * 20435000002184). For a non-SIRS type, any of these terms at the START of a word in the
 * lower-cased description flags the report for manager triage (delta review B3). Stems
 * ("injur", "bruis", "fractur") match any word they begin; WHOLE words ("head", "forehead") must
 * also end there, so "headache" and "ahead" do not match. It is a flag, never a classification.
 */
export const SIRS_TRIAGE_KEYWORDS = [
  'force',
  'assault',
  'sexual',
  'abuse',
  'threat',
  'neglect',
  'financial',
  'steal',
  'missing',
  'absence',
  'restrictive',
  'restraint',
  'death',
  'police',
  'injur',
  'bruis',
  'blood',
  'unexplain',
  'skin tear',
  'fractur',
  'head',
  'forehead',
] as const;

/** Keywords that must be whole words (`\M` in the SQL pattern). */
export const SIRS_TRIAGE_WHOLE_WORDS: readonly string[] = ['head', 'forehead'];

/** The exact PostgreSQL ARE pattern used by the migration (`\m` word start, `\M` word end). */
export const SIRS_TRIAGE_POSIX_PATTERN = `\\m(${SIRS_TRIAGE_KEYWORDS.map((k) =>
  SIRS_TRIAGE_WHOLE_WORDS.includes(k) ? `${k}\\M` : k,
).join('|')})`;

// PostgreSQL word characters are letters, digits and underscore; the JS twin uses the same set.
const SIRS_TRIAGE_REGEX = new RegExp(
  `(?<![\\p{L}\\p{N}_])(?:${SIRS_TRIAGE_KEYWORDS.map((k) =>
    SIRS_TRIAGE_WHOLE_WORDS.includes(k) ? `${k}(?![\\p{L}\\p{N}_])` : k,
  ).join('|')})`,
  'u',
);

export function descriptionNeedsSirsTriage(description: string | null | undefined): boolean {
  return SIRS_TRIAGE_REGEX.test((description ?? '').toLowerCase());
}

/** Optional narrative for standalone care capture; never retain it for another type/subtype. */
export function medicationSubtypeOtherFields(
  incidentType: string | null | undefined,
  incidentSubtype: string | null | undefined,
  details: unknown,
): Record<string, string> {
  return incidentType === 'MEDICATION_ERROR' &&
    incidentSubtype === 'OTHER' &&
    typeof details === 'string' &&
    details.trim()
    ? { medicationSubtypeOtherDetails: details.trim() }
    : {};
}

/** Operational type, never an additional SIRS category. */
export const MEDICAL_EPISODE_LABEL = 'Medical episode';
export const MEDICAL_EPISODE_HELP =
  'An acute health event, such as fainting or a seizure. Use Medication error for a medication adverse reaction, Unexplained injury for an injury without a known cause, and Unexpected death for a death. Record the facts for SIRS review.';
/** Exact incident CHECK vocabulary, including historical and system-only tokens. */
export const INCIDENT_DESCRIPTIVE_TYPE_TOKENS = [
  'UNREASONABLE_USE_OF_FORCE',
  'UNLAWFUL_SEXUAL_CONTACT',
  'PSYCHOLOGICAL_EMOTIONAL_ABUSE',
  'STEALING_FINANCIAL_COERCION',
  'NEGLECT',
  'INAPPROPRIATE_RESTRICTIVE_PRACTICES',
  'UNEXPLAINED_ABSENCE',
  'UNEXPECTED_DEATH',
  'OTHER_SERIOUS',
  'SERIOUS_HARM',
  'FALL',
  'MEDICATION_ERROR',
  'INFECTION',
  'PRESSURE_INJURY',
  'BEHAVIOURAL',
  'OTHER',
  'SKIN_INJURY',
  'UNEXPLAINED_INJURY',
  'S8_WITNESS_OVERDUE',
  'MEDICAL_EPISODE',
] as const;
export function validAdditionalIncidentTypes(
  value: unknown,
  primary?: string | null,
): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= 4 &&
    new Set(value).size === value.length &&
    value.every(
      (v) =>
        typeof v === 'string' &&
        v !== primary &&
        (INCIDENT_DESCRIPTIVE_TYPE_TOKENS as readonly string[]).includes(v),
    )
  );
}
